import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { ShortcutDefinition, useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { loadCustomShortcuts, CustomShortcut } from '../lib/shortcutCustomization';

interface KeyboardShortcutsContextValue {
  shortcuts: ShortcutDefinition[];
  registerShortcuts: (shortcuts: ShortcutDefinition[]) => void;
  unregisterShortcuts: (keys: string[]) => void;
  showHelp: boolean;
  setShowHelp: (show: boolean) => void;
  toggleHelp: () => void;
  lastConvertedColor: string | null;
  setLastConvertedColor: (color: string | null) => void;
  modalOpen: boolean;
  setModalOpen: (open: boolean) => void;
  customShortcuts: Record<string, CustomShortcut>;
  reloadCustomShortcuts: () => void;
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextValue | undefined>(undefined);

interface KeyboardShortcutsProviderProps {
  children: ReactNode;
}

export const KeyboardShortcutsProvider: React.FC<KeyboardShortcutsProviderProps> = ({ children }) => {
  const [shortcuts, setShortcuts] = useState<ShortcutDefinition[]>([]);
  const [showHelp, setShowHelp] = useState(false);
  const [lastConvertedColor, setLastConvertedColor] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [customShortcuts, setCustomShortcuts] = useState<Record<string, CustomShortcut>>(() => loadCustomShortcuts());

  // Load custom shortcuts on mount and listen for changes
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'chromis-custom-shortcuts') {
        setCustomShortcuts(loadCustomShortcuts());
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const reloadCustomShortcuts = useCallback(() => {
    setCustomShortcuts(loadCustomShortcuts());
  }, []);

  const registerShortcuts = useCallback((newShortcuts: ShortcutDefinition[]) => {
    setShortcuts((prev) => {
      // Remove any existing shortcuts with the same key combination
      const filtered = prev.filter((existing) => {
        return !newShortcuts.some((newShortcut) => {
          return (
            existing.key === newShortcut.key &&
            existing.ctrlOrCmd === newShortcut.ctrlOrCmd &&
            existing.shift === newShortcut.shift &&
            existing.alt === newShortcut.alt
          );
        });
      });
      return [...filtered, ...newShortcuts];
    });
  }, []);

  const unregisterShortcuts = useCallback((keys: string[]) => {
    setShortcuts((prev) => prev.filter((shortcut) => !keys.includes(shortcut.key)));
  }, []);

  const toggleHelp = useCallback(() => {
    setShowHelp((prev) => !prev);
  }, []);

  // Apply custom shortcuts to registered shortcuts
  const mergedShortcuts = shortcuts.map((shortcut) => {
    // Find if there's a custom shortcut for this action
    const customShortcut = Object.values(customShortcuts).find((cs) => {
      // Match by description or category to identify the action
      return shortcut.description.toLowerCase().includes(cs.id.replace(/-/g, ' '));
    });

    if (customShortcut) {
      return {
        ...shortcut,
        key: customShortcut.key,
        ctrlOrCmd: customShortcut.ctrlOrCmd,
        shift: customShortcut.shift,
        alt: customShortcut.alt,
      };
    }

    return shortcut;
  });

  // Initialize keyboard shortcuts hook with merged shortcuts
  useKeyboardShortcuts({ shortcuts: mergedShortcuts, enabled: true });

  const value: KeyboardShortcutsContextValue = {
    shortcuts,
    registerShortcuts,
    unregisterShortcuts,
    showHelp,
    setShowHelp,
    toggleHelp,
    lastConvertedColor,
    setLastConvertedColor,
    modalOpen,
    setModalOpen,
    customShortcuts,
    reloadCustomShortcuts,
  };

  return (
    <KeyboardShortcutsContext.Provider value={value}>
      {children}
    </KeyboardShortcutsContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useKeyboardShortcutsContext = (): KeyboardShortcutsContextValue => {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error('useKeyboardShortcutsContext must be used within KeyboardShortcutsProvider');
  }
  return context;
};
